import { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import type { FileRejection } from 'react-dropzone'
import { Button, Chip } from '@heroui/react'

import { UploadIcon } from '../../../../components'

interface FileWithPreview extends File {
  preview: string
}

interface FileDropzoneProps {
  onFilesChange: (files: File[]) => void
  accept?: Record<string, string[]>
  maxFiles?: number
  maxSize?: number
  disabled?: boolean
}

export const FileDropzone = ({
  onFilesChange,
  accept = { 'image/*': ['.gif', '.mp4', '.webp'] },
  maxFiles = 1,
  maxSize = 50 * 1024 * 1024, // 50MB
  disabled = false,
}: FileDropzoneProps) => {
  const [files, setFiles] = useState<FileWithPreview[]>([])
  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([])

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (acceptedFiles?.length) {
        const newFiles = acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
        setFiles(newFiles)
        onFilesChange(acceptedFiles)
      }

      if (rejectedFiles?.length) {
        setRejectedFiles(rejectedFiles)
      }
    },
    [onFilesChange]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles,
    maxSize,
    disabled,
  })

  const removeFile = (fileName: string) => {
    setFiles((current) => {
      const filtered = current.filter((file) => file.name !== fileName)
      // Defer the callback to avoid synchronous state updates during render
      setTimeout(() => onFilesChange(filtered), 0)
      return filtered
    })
  }

  const removeRejected = (fileName: string) => {
    setRejectedFiles((current) =>
      current.filter(({ file }) => file.name !== fileName)
    )
  }

  useEffect(() => {
    // Clean up previews on unmount
    const cleanupPreviews = () => {
      files.forEach((file) => URL.revokeObjectURL(file.preview))
    }
    return () => cleanupPreviews()
  }, [files])

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <h4 className="text-medium font-medium">Upload GIF</h4>
      </div>
      {/* Dropzone Area */}
      <div
        {...getRootProps()}
        className={`flex flex-col items-center justify-center p-8 text-center space-y-4 transition-all duration-200 rounded-lg ${
          isDragActive
            ? 'border-primary-500 bg-primary-50 scale-[1.02]'
            : 'border-dashed border-2 border-default-300 hover:border-default-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <input {...getInputProps()} />

        <UploadIcon size="md" />

        {/* Upload Text */}
        <div className="space-y-2">
          <p className="text-lg font-medium text-default-700">
            {isDragActive ? 'Drop your GIF here!' : 'Drag & drop your GIF here'}
          </p>
          <p className="text-sm text-default-500">or click to browse files</p>
          <p className="text-xs text-default-400">
            Supports: GIF, PNG, JPG, WEBP (max {maxSize / 1024 / 1024}MB)
          </p>
        </div>
      </div>

      {/* Accepted Files Preview */}
      {files.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-default-700 my-2">
            Selected File{files.length > 1 ? 's' : ''}
          </h4>
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.name}
                className="flex items-center justify-between p-3 bg-default-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  {file.type.startsWith('image/') && (
                    <img
                      src={file.preview}
                      alt={file.name}
                      className="w-12 h-12 object-cover rounded-md"
                      onLoad={() => URL.revokeObjectURL(file.preview)}
                    />
                  )}
                  <div>
                    <p className="text-sm font-medium text-default-700 truncate max-w-48">
                      {file.name}
                    </p>
                    <p className="text-xs text-default-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  onPress={() => removeFile(file.name)}
                  className="min-w-unit-8 w-8 h-8 p-0"
                >
                  ✕
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rejected Files */}
      {rejectedFiles.length > 0 && (
        <div className="border-danger-200">
          <h4 className="text-sm font-medium text-danger-600 mb-3">
            Rejected Files
          </h4>
          <div className="space-y-2">
            {rejectedFiles.map(({ file, errors }) => (
              <div
                key={file.name}
                className="flex items-center justify-between p-3 bg-danger-50 rounded-lg"
              >
                <div>
                  <p className="text-sm font-medium text-danger-700 truncate max-w-48">
                    {file.name}
                  </p>
                  <div className="space-y-1">
                    {errors.map((error) => (
                      <Chip
                        key={error.code}
                        size="sm"
                        color="danger"
                        variant="flat"
                        className="text-xs"
                      >
                        {error.message}
                      </Chip>
                    ))}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  onPress={() => removeRejected(file.name)}
                  className="min-w-unit-8 w-8 h-8 p-0"
                >
                  ✕
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
